export interface VersionFields {
  status?: string | null;
  versionNumber?: number | null;
}

export type VersionReader<T> = (version: T) => VersionFields;

// Versions shaped like VersionFields need no reader; any other shape passes one
type Reader<T> = [T] extends [VersionFields] ? [read?: VersionReader<NoInfer<T>>] : [read: VersionReader<NoInfer<T>>];

const readerOf = <T>(reader: [read?: VersionReader<T>]): VersionReader<T> => reader[0] ?? ((v) => v as VersionFields);

export const fromSceneRow: VersionReader<{
  scene_instance_status?: string | null;
  scene_instance_version_number?: number | null;
}> = (row) => ({ status: row.scene_instance_status, versionNumber: row.scene_instance_version_number });

const EDITABLE_STATUSES = new Set(['draft', 'template']);

const newestFirst = <T>(versions: readonly T[], read: VersionReader<T>) =>
  [...versions].sort((a, b) => (read(b).versionNumber ?? 0) - (read(a).versionNumber ?? 0));

const newestWith = <T>(versions: readonly T[], read: VersionReader<T>, keep: (status: string) => boolean) =>
  newestFirst(versions, read).find((v) => keep(read(v).status ?? '')) ?? null;

const live = <T>(versions: readonly T[], read: VersionReader<T>) => newestWith(versions, read, (s) => s === 'live');
const pending = <T>(versions: readonly T[], read: VersionReader<T>) => newestWith(versions, read, (s) => s === 'pending');
const draft = <T>(versions: readonly T[], read: VersionReader<T>) => newestWith(versions, read, (s) => EDITABLE_STATUSES.has(s));

export function sortNewestFirst<T>(versions: readonly T[], ...reader: Reader<T>): T[] {
  return newestFirst(versions, readerOf(reader));
}

export function pickLiveVersion<T>(versions: readonly T[], ...reader: Reader<T>): T | null {
  return live(versions, readerOf(reader));
}

export function pickPendingVersion<T>(versions: readonly T[], ...reader: Reader<T>): T | null {
  return pending(versions, readerOf(reader));
}

export function pickPublishedVersion<T>(versions: readonly T[], ...reader: Reader<T>): T | null {
  const read = readerOf(reader);
  return live(versions, read) ?? pending(versions, read);
}

export function pickNewestDraft<T>(versions: readonly T[], ...reader: Reader<T>): T | null {
  return draft(versions, readerOf(reader));
}

export function pickNewestVersion<T>(versions: readonly T[], ...reader: Reader<T>): T | null {
  return newestFirst(versions, readerOf(reader))[0] ?? null;
}

export function pickCurrentVersion<T>(versions: readonly T[], ...reader: Reader<T>): T | null {
  const read = readerOf(reader);
  return draft(versions, read) ?? live(versions, read) ?? pending(versions, read) ?? newestWith(versions, read, (s) => s === 'locked');
}

export function withoutArchived<T>(versions: readonly T[], ...reader: Reader<T>): T[] {
  const read = readerOf(reader);
  return versions.filter((v) => read(v).status !== 'archived');
}
